import { get, set, find, merge, map, isUndefined, isArray } from 'lodash';
import dayjs from 'dayjs';
import { sendEventTrackingData } from 'mdt-core-components/src/helpers/commonUtils.js';
import {
    fetchReturnReasons,
    fetchOrderFromReturnCart,
    postReturnOrderSubmit,
    fetchPackingInformation,
    fetchReturnableItemsForOrder,
    fetchReturnCartItems,
    sendRemoveItemFromReturnsCart,
    postItemsToReturnCart,
    fetchReturnsWishlist,
    fetchReturnEligible,
    sendUpdateItemForReturnsWizard,
} from '../services/returnsService.js';
import {
    setReturnsReasons,
    setReturnItems,
    setODReturnItems,
    setODReturnInvoices,
    setReturnsWishlist,
    setReturnsCartCount,
    setOrder,
    setShipTos,
    setReturnsPackages,
    setSecondContact,
    setOrderTotalPrice,
} from '../reducers/returnsReducer.js';

const handleError = (error) => {
    console.log('An error happened, please pass it onto the global error handler', error);
    console.error('An error happened, please pass it onto the global error handler', error);
    throw Error('An error happened');
};

export const getReturnsWishlist = () => {
    return (dispatch) => {
        return fetchReturnsWishlist()
            .then((res) => {
                const eventParamObj = {
                    eventCategory: 'ReturnOrderWishlist',
                    eventAction: 'Return OrderDetail',
                    eventLabel: 'Return Order Wishlist',
                    processLabels: false,
                    customInfo: {
                        wishListId: res[0].wishListId,
                    },
                };
                sendEventTrackingData(eventParamObj);
                dispatch(setReturnsWishlist(res[0].wishListId));
            })
            .catch(handleError.bind(dispatch));
    };
};

export const isReturnCheckoutFlow = () => {
    return (dispatch, getState) => {
        return get(getState(), 'location.query.isReturnOrder', false);
    };
};

// NOTE: This transforms the address from the order to the address that the API understands 20200121:Alevale
const buildReturnShipTo = (order, shipTos) => {
    let shipToAddress = find(shipTos, (shipTo) => shipTo.id === order.shipToID);

    // NOTE: If the user has put a custom address 20200117:Alevale
    if (get(order, 'submissionUser.address') === -1) {
        const { shipTo } = get(order, 'shipmentDetails', { shipTo: { address: {} } });
        // NOTE: Merge the address with the shipTo address to keep the country because a user can't request pickup in a
        // different country 20200117:Alevale
        const address = {
            line1: shipTo.address.line1,
            line2: shipTo.address.line2,
            phone: get(order, 'submissionUser.phoneNumber'),
            cellPhone: get(order, 'submissionUser.phoneNumber'),
            postalCode: shipTo.address.zip,
            streetName: `${shipTo.address.line1} ${shipTo.address.line2}`,
            town: shipTo.address.town,
            regionCode: '',
            regionName: shipTo.address.state,
            firstName: get(order, 'submissionUser.firstName'),
            middleName: '',
            lastName: get(order, 'submissionUser.lastName'),
            addrOverride: true,
            primaryContact: get(order, 'submissionUser.name'),
        };
        // NOTE: Reuse the shipTo structure for this address 20200121:Alevale
        shipToAddress.address = merge(shipTo.address, address);

        // NOTE: If no address was selected as an override, in the return checkout dropdown then leave the default address 20200204:Alevale
    } else if (
        get(order, 'submissionUser.address') !== -1 &&
        !isUndefined(get(order, 'submissionUser.address', undefined))
    ) {
        shipToAddress = find(
            shipTos,
            (shipTo) => shipTo.id === get(order, 'submissionUser.address')
        );
    }

    return shipToAddress;
};

// NOTE: This converts the frontend data to the data schema that the backend needs and then sends it 20200128:Alevale
export const submitOrder = () => {
    return (dispatch, getState) => {
        const { order, shipTos, wishlistId, returnReasons } = getState().returnsReducer;
        const { defaultCustomer, user } = getState().meDetails;
        const returnCheckoutConfiguration = get(
            getState(),
            'formConfig.featureConfiguration.ReturnCheckoutFlow',
            {}
        );
        let shipTo = {};

        if (get(returnCheckoutConfiguration, 'configuration.displayPickupAddress')) {
            shipTo = buildReturnShipTo(order, shipTos);
        }

        const supplier = {
            defaultPayer: get(order, 'invoiceDetails.payer'),
            defaultBillTo: get(order, 'invoiceDetails.billTo'),
            defaultShipTo: shipTo,
            salesOrgId: '-',
            id: get(order, 'entriesList[0].supplierInfo.id'),
        };

        order.shipToID = get(order, 'supplier.defaultShipTo.id', order.shipToID);

        const packingProposals = map(order.submissionUser.packages, (value, key) => {
            return {
                material: key,
                quantity: value,
            };
        });

        const selectedReturnReason = returnReasons.find(
            (r) => r.subOrderReasonCode === order.submissionUser.returnReason
        );
        const orderReason = selectedReturnReason ? selectedReturnReason.code : '';
        const subOrderReason = selectedReturnReason ? selectedReturnReason.subOrderReasonCode : '';

        if (get(returnCheckoutConfiguration, 'configuration.displayPickupDate')) {
            // NOTE: Make the time be in the right UTC dayjs 20200305:Alevale
            order.reqDelDate = dayjs(order.submissionUser.pickupDate).add(dayjs().utcOffset(), 'm');
        }

        order.shipToAddressOverride =
            supplier.defaultShipTo && supplier.defaultShipTo.address
                ? supplier.defaultShipTo.address.addrOverride
                : false;
        order.contactPerson = {
            id: shipTo.id,
        };

        // NOTE: This should be coming from the payload from the notes API, that would post the note to an endpoint
        // with he draft order, but since the returns orders don't have drafts, it all has to be built in the FE
        // 20200220:Alevale
        const noteSummary = {
            totalNotes: 1,
            orderNotes: [
                {
                    posNumber: 0,
                    notes: order.submissionUser.notes,
                    typeCode: order.submissionUser.notesCode,
                    comments: order.submissionUser.notes,
                },
            ],
        };

        // NOTE: Build request body payload for multiple email address functionality
        let primaryEmailAddress = '';
        const secondaryEmailAddresses = [];

        // NOTE: Extracting addtional emails for request body
        if (isArray(order.submissionUser.email)) {
            order.submissionUser.email.map((email, i) => {
                if (i === 0) {
                    primaryEmailAddress = email;
                } else {
                    secondaryEmailAddresses.push(email);
                }
            });
        } else {
            primaryEmailAddress = order.submissionUser.email;
        }

        const contactPersonAddress = {
            firstName: order.submissionUser.name,
            email: primaryEmailAddress,
            additionalEmails: secondaryEmailAddresses,
            phone: order.submissionUser.phoneNumber,
            countryIsoCode: defaultCustomer.country.isoCode,
            communicationLanguage: get(user, 'culture', '').replace('-', '_'),
        };

        order.submissionUser.email = primaryEmailAddress;

        // NOTE: Add to each element the currencyIsoCode as currencyId 20200218:Alevale
        order.entriesList.forEach((entry) => {
            entry.currencyId = order.currencyIsoCode;
        });

        // NOTE: For the US return checkout flow we are not sending the shipto,
        // as the US has a different logic to get the shipto please, if the logic
        // changes this might be an unwanted side effect 20200916:Alevale
        if (get(returnCheckoutConfiguration, 'configuration.displayShippingLabelCount')) {
            order.ShippingLabelCount = order.submissionUser.returnShippingLabels;
            supplier.defaultShipTo = {};
        }

        const data = {
            orders: [].concat(order),
            cartId: wishlistId,
            purchaseOrderNumber: order && order.po,
            orderReason,
            subOrderReason,
            contactPersonAddress,
            noteSummary,
            packingProposals,
            supplier,
            reqDelDate: order.reqDelDate,
            contactPersonOverride: true,
        };

        const eventParamObj = {
            eventCategory: 'returnOrderSubmit',
            eventAction: 'Return Checkout',
            eventLabel: 'Return Order Submit',
            processLabels: false,
            customInfo: {
                data: data,
            },
        };
        sendEventTrackingData(eventParamObj);
        return postReturnOrderSubmit(data)
            .then((res) => {
                // NOTE: Redirect to the order confirmation page 20200127:Alevale
                dispatch({
                    type: 'ORDERCONFIRMATION',
                    payload: { orderIds: res.orderIds },
                    query: {
                        isReturnOrder: true,
                        orderType: data?.orders[0]?.orderType, // TODO: this is temporary solution, Once return wizard will integrated then we can handle multiple order type
                    },
                });
                dispatch(getReturnsCart());
                return true;
            })
            .catch(handleError.bind(dispatch));
    };
};

export const submitReturnWizardOrder = () => {
    return (dispatch, getState) => {
        const { order, shipTos, returnReasons, showSecondContact, wishlistId } =
            getState().returnsReducer;
        const { contactDetails, pickupDetails, returnNotes, selectedReason } =
            getState().returnCheckoutReducer;
        const { user, customer } = getState().meDetails;

        let shipToAddress = find(
            shipTos,
            (shipTo) => shipTo.id === pickupDetails.selectedPickupAddress
        );

        // NOTE: If the user has add a New address
        if (!!pickupDetails.isAddAddress) {
            shipToAddress = {
                address: {
                    name: pickupDetails?.newAddress?.addressName,
                    line1: pickupDetails?.newAddress?.address,
                    line2: '',
                    streetName: pickupDetails?.newAddress?.department,
                    phone: contactDetails.contactPhone,
                    cellPhone: contactDetails.contactPhone,
                    postalCode: pickupDetails?.newAddress?.postalCode || '',
                    town: pickupDetails?.newAddress?.city,
                    regionCode: pickupDetails?.newAddress?.state,
                    regionName: pickupDetails?.newAddress?.stateName,
                    firstName: '',
                    middleName: '',
                    lastName: '',
                    addrOverride: true,
                    primaryContact: contactDetails.contactName,
                    country: get(shipTos, '[0].address.country', {}),
                },
            };
        }

        const supplier = {
            defaultPayer: get(customer, 'supplierCustomerContracts[0].defaultPayer', {}),
            defaultBillTo: get(customer, 'supplierCustomerContracts[0].defaultBillTo', {}),
            defaultShipTo: shipToAddress,
            salesOrgId: '-',
            id: get(customer, 'supplierCustomerContracts[0].supplier.id', ''),
        };

        const packingProposals = map(pickupDetails.parcel, (value, key) => {
            return {
                material: key,
                quantity: value,
            };
        });

        const selectedReturnReason = returnReasons.find(
            (r) => r.subOrderReasonCode === selectedReason
        );
        const orderReason = selectedReturnReason ? selectedReturnReason.code : '';
        const subOrderReason = selectedReturnReason ? selectedReturnReason.subOrderReasonCode : '';

        const noteSummary = {
            totalNotes: 1,
            orderNotes: [
                {
                    posNumber: 0,
                    notes: returnNotes,
                },
            ],
        };

        const contactPersonAddress = {
            firstName: contactDetails.contactName,
            email: contactDetails.contactEmail,
            additionalEmails: [],
            phone: contactDetails.contactPhone,
            countryIsoCode: customer.country.isoCode,
            communicationLanguage: get(user, 'culture', '').replace('-', '_'),
        };

        let data = {
            orders: order,
            contactPersonAddress,
            cartId: wishlistId,
            packingProposals,
            orderReason,
            subOrderReason,
            noteSummary,
            reqDelDate: pickupDetails?.pickupDate
                ? dayjs(pickupDetails?.pickupDate).add(dayjs().utcOffset(), 'm')
                : '',
            supplier,
            soldToId: customer?.soldTo?.id || '',
            pickupAddressNote: pickupDetails.pickupDetailNote || '',
            contactPersonOverride: true,
        };

        // NOTE:  If Second Contact is required then we are adding secondContact details in payload
        if (!!showSecondContact) {
            data = {
                ...data,
                ...{
                    secondContactName: contactDetails.secondaryContactName || '',
                    secondContactEmail: contactDetails.secondaryContactEmail || '',
                    secondContactPhone: contactDetails.secondaryContactPhone || '',
                },
            };
        }

        const eventParamObj = {
            eventCategory: 'returnOrderSubmit',
            eventAction: 'Return Checkout',
            eventLabel: 'Return Order Submit',
            processLabels: false,
            customInfo: {
                data: data,
            },
        };
        sendEventTrackingData(eventParamObj);
        return postReturnOrderSubmit(data)
            .then((res) => {
                // NOTE: Redirect to the order confirmation page
                dispatch({
                    type: 'ORDERCONFIRMATION',
                    payload: { orderIds: res.orderIds },
                    query: {
                        isReturnOrder: true,
                    },
                });
                dispatch(getReturnsCart());
                return true;
            })
            .catch(handleError.bind(dispatch));
    };
};

export const getOrderFromReturnCart = (wizardFlag) => {
    return (dispatch, getState) => {
        const { wishlistId, returnItems } = getState().returnsReducer;

        const data = {
            cartId: wishlistId,
            entryNumbers: returnItems.filter((i) => i.selected).map((i) => i.entryId),
        };

        const eventParamObj = {
            eventCategory: 'getOrderFromReturnCart',
            eventAction: 'Return Cart',
            processLabels: false,
            customInfo: {
                data: data,
            },
        };
        sendEventTrackingData(eventParamObj);
        // NOTE: This parses the return order from the backend to what a normal
        // order should look like in the frontend (since the backend doesn't
        // send the fields in the API if they are empty 20200128:Alevale
        return fetchOrderFromReturnCart(data)
            .then((res) => {
                const { orders, shipTos, totalPrice, showSecondContact } = res;

                orders.map((order) => {
                    // NOTE: Set the order as a return order 20191209:Alevale
                    order.isReturnOrder = true;
                    order.id = '-';
                    order.typeCode = order.orderType;
                    order.supplier = {
                        salesOrgId: '-',
                    };

                    order.shipmentDetails = {
                        shippingCondition: {},
                    };

                    order.currency = {
                        isocode: order.currencyIsoCode,
                    };

                    order.totalPrice = merge(order.totalPrice, {
                        currency: {
                            isocode: order.currencyIsoCode,
                        },
                        subTotal: order.subTotal,
                    });

                    order.submissionUser = Object.assign(order.submissionUser, {
                        notes: '',
                        notesCode: '',
                        returnReason: '',
                        packages: {},
                    });
                    return order;
                });

                const eventParamObj = {
                    eventCategory: 'getOrderFromReturnCart',
                    eventAction: 'Return Cart',
                    eventLabel: `Selected order type ${orders[0].orderType}`,
                    processLabels: false,
                    customInfo: {
                        order: orders,
                    },
                };
                sendEventTrackingData(eventParamObj);
                dispatch(setOrder(!!wizardFlag ? orders : orders[0]));
                dispatch(setShipTos(shipTos));
                dispatch(setSecondContact(!!showSecondContact));
                dispatch(setOrderTotalPrice(totalPrice));
            })
            .catch(handleError.bind(dispatch));
    };
};

export const getReturnPackingInformation = (wizardFlag) => {
    return (dispatch, getState) => {
        const { order } = getState().returnsReducer;
        const customer = get(getState(), 'meDetails.customer', {});
        const supplierInfo = !!wizardFlag
            ? get(customer, 'supplierCustomerContracts[0].supplier.id', '')
            : get(order, 'entriesList[0].supplierInfo.id', '');

        // NOTE: We need to have the supplierID so that we know which packages do we have available for that order, the
        // supplierID is extracted from the entries in the order. Because of this we need to verify that all the items
        // in the return order are indeed from the same supplier. The return order is supposed to have only 1 supplier.
        // 20200811:Alevale
        if (
            !wizardFlag &&
            order.entriesList.some((order) => order.supplierInfo.id !== supplierInfo)
        ) {
            throw Error(
                'There are several suppliers in the items of the return order. At the time this was build the suppliers were all supposed to be the same. Please adjust the logic'
            );
        }

        if (!supplierInfo) {
            throw Error('Missing required supplierInfo');
        }

        const eventParamObj = {
            eventCategory: 'fetchPackingInformation',
            eventAction: 'Return Checkout',
            processLabels: false,
        };
        sendEventTrackingData(eventParamObj);
        // NOTE: The parameter for the return packages is hardcoded as "VERP" but the API is generic 20191209:Alevale
        return fetchPackingInformation('VERP', supplierInfo)
            .then((packages) => {
                packages.map((packages) => {
                    // NOTE: Initialize the amount of packages to 0 (not sure
                    // if the API should already have a saying on this) 20191212:Alevale
                    packages.amount = 0;
                    return packages;
                });
                dispatch(setReturnsPackages(packages));
            })
            .catch(handleError.bind(dispatch));
    };
};

export const getReturnReasons = () => {
    return (dispatch, getState) => {
        const territoryId =
            getState().meDetails &&
            getState().meDetails.customer &&
            getState().meDetails.customer.territory
                ? getState().meDetails.customer.territory.id
                : 'default';

        return fetchReturnReasons(territoryId)
            .then((response) => {
                dispatch(setReturnsReasons(response));
            })
            .catch(handleError.bind(dispatch));
    };
};

export const saveReturnOrderNotes = (orderNotes) => {
    const eventParamObj = {
        eventCategory: 'SaveReturnOrderNote',
        eventAction: 'Return Checkout',
        processLabels: false,
    };
    sendEventTrackingData(eventParamObj);
    return (dispatch) => {
        dispatch(setOrderFieldValue('submissionUser.notes', get(orderNotes, '[0].notes', '')));
        dispatch(setOrderFieldValue('submissionUser.notesCode', get(orderNotes, '[0].name', '')));
    };
};

export const setOrderFieldValue = (path, value) => {
    return (dispatch, getState) => {
        const { order } = getState().returnsReducer;
        dispatch(setOrder(set(order, path, value)));
    };
};

export const getReturnsCart = () => {
    return async (dispatch, getState) => {
        const { wishlistId, returnItems } = getState().returnsReducer;

        // NOTE: Sometimes the wishlistId is not there, if so then query it and re-trigger this function 20200213:Alevale
        if (!wishlistId) {
            return dispatch(getReturnsWishlist())
                .then(() => dispatch(getReturnsCart()))
                .catch(handleError.bind(dispatch));
        }

        return fetchReturnCartItems(wishlistId)
            .then((cart) => {
                const res = cart.entries.map((entry) => {
                    entry.checkbox = true;
                    entry.selected = true;
                    entry.orderNo = entry.entryCFN;
                    entry.description = entry.entryName;
                    entry.batchNo = entry.batch;
                    entry.amount = entry.entryQty;
                    entry.type = entry.entryUoM.code;
                    entry.originalQty =
                        returnItems.find((item) => item.entryId === entry.entryId)?.originalQty ||
                        entry.entryQty; // Track original qty from existing return reducer, if the item is not available in reducer then entryQty will be originalQty

                    return entry;
                });

                dispatch(setReturnItems(res));
                dispatch(setReturnsCartCount(cart.productCount));
            })
            .catch(handleError.bind(dispatch));
    };
};

export const setReturnWizardItems = (data) => {
    return (dispatch) => {
        dispatch(setReturnItems(data));
    };
};

export const addItemsToReturnCart = () => {
    return (dispatch, getState) => {
        const { ODReturnItems, wishlistId, ODOrder, ODInvoices } = getState().returnsReducer;
        const selectedItems = ODReturnItems.filter((returnItem) => returnItem.selected);

        const itemsForCart = selectedItems.map((selectedItem) => {
            const [ODEntry] = ODOrder.entriesList.filter(
                (entry) => entry.entryCFN === selectedItem.cfn
            );
            const [ODInvoice] = ODInvoices.filter((invoice) => {
                return invoice.entries.some((entry) => entry.id === selectedItem.id);
            });

            if (!ODEntry || !ODInvoice) {
                throw Error('Unable to find item in payloads, API incorrect');
            }

            return {
                CFN: ODEntry.entryCode,
                cfnDescription: selectedItem.description,
                productId: selectedItem.internalId,
                quantity: selectedItem.amount,
                uom: selectedItem.salesUOM,
                contractId: '',
                customerText: '',
                batchNumber: selectedItem.batchId,
                basePrice: selectedItem.netAmount / selectedItem.invoiceQuantity,
                facingPrice: {
                    value:
                        (selectedItem.netAmount / selectedItem.invoiceQuantity +
                            selectedItem.taxAmount / selectedItem.invoiceQuantity) *
                        selectedItem.amount,
                },
                totalPrice: {
                    value:
                        (selectedItem.netAmount / selectedItem.invoiceQuantity) *
                        selectedItem.amount,
                },
                useByDate: '',
                orderType: {
                    key: '',
                },
                subOrderType: {
                    key: '',
                },
                orderDomain: {
                    key: '',
                },
                supplierId: ODOrder.supplier.id,
                currencyIsoCode: ODOrder.currency.isocode,
                referenceOrderType: selectedItem.refProcessingTypeCode,
                referenceDocNumber: ODInvoice.id,
                referenceDocLineNumber: selectedItem.id,
                referenceDocCategory: ODInvoice.docCat,
                referencePONumber: selectedItem.buyerDocId,
                referenceOrderNumber: selectedItem.refSalesOrderId,
            };
        });

        const data = {
            purchaseOrderNumber: ODOrder.po,
            soldToId: ODOrder.soldtoID,
            billToId: ODOrder.billToID,
            payerId: ODOrder.payerID,
            shipToId: ODOrder.shipToID,
            wishItems: itemsForCart,
        };

        return postItemsToReturnCart(data, wishlistId)
            .then(() => {
                const aux = ODReturnItems.map((item) => {
                    item.duplicateItem = false;
                    return item;
                });
                dispatch(setODReturnItems(aux));
                return true;
            })
            .catch(handleError.bind(dispatch));
    };
};

export const getReturnableItemsForOrder = (orderId) => {
    const eventParamObj = {
        eventCategory: 'returnableOrder',
        eventAction: 'Return Details',
        processLabels: false,
        customInfo: {
            orderId: orderId,
        },
    };
    sendEventTrackingData(eventParamObj);
    return (dispatch) => {
        return fetchReturnableItemsForOrder(orderId)
            .then((invoices) => {
                // NOTE: The API returns a list of invoices,
                // that contain different entries each.
                // Each of the entries is a returnable item 20191108:Alevale
                const entries = invoices.reduce((acum, invoice) => {
                    const uniqueEntries = map(invoice.entries, (e) => {
                        // NOTE: Create a unique ID for handling this in the UI because the entries in an invoice
                        // sometimes can seem duplicate. This happens when too many items are ordered and are
                        // sent as different orders. Which ends up with 2 different orders with 2 identical entries 20210126:Alevale
                        e.selectableId = `${invoice.id}-${e.id}`;
                        return e;
                    });
                    return acum.concat(uniqueEntries);
                }, []);

                const eventParamObj = {
                    eventCategory: 'returnableItemsForOrder',
                    eventAction: 'Return Details',
                    processLabels: false,
                    customInfo: {
                        invoices: invoices,
                    },
                };
                sendEventTrackingData(eventParamObj);
                const items = entries.map((entry) => {
                    entry.selected = false;
                    // NOTE: Entry should be disabled if it has either an error message or selectable items are 0 20200214:Alevale
                    entry.disabled = !!entry.rpIndicator || entry.returnableQuantity === 0;
                    entry.amount = entry.returnableQuantity;
                    entry.duplicateItem = false;
                    return entry;
                });
                dispatch(setODReturnItems(items));
                dispatch(setODReturnInvoices(invoices));
            })
            .catch(handleError.bind(dispatch));
    };
};

export const removeItemFromReturnsCart = (itemsToDelete) => {
    return (dispatch, getState) => {
        const { returnItems, wishlistId } = getState().returnsReducer;
        // NOTE: Get the entryId of the items that should be deleted.
        // The API expects an array with numbers which are the position
        // of the elements that you are going to delete 20191203:Alevale
        itemsToDelete =
            itemsToDelete ||
            returnItems
                .filter((i) => i.selected)
                .map((i) => {
                    return i.entryId;
                });

        // NOTE: There is no need to call the API 20200124:Alevale
        if (itemsToDelete.length <= 0) {
            return;
        }

        const eventParamObj = {
            eventCategory: 'removeItemFromReturnsCart',
            eventAction: 'Return Cart',
            processLabels: false,
            customInfo: {
                wishlistId: wishlistId,
                itemsToDelete: itemsToDelete,
            },
        };
        sendEventTrackingData(eventParamObj);
        return sendRemoveItemFromReturnsCart(wishlistId, itemsToDelete)
            .then(() => {
                dispatch(getReturnsCart());
            })
            .catch(handleError.bind(dispatch));
    };
};

export const updateReturnItemQty = (data) => {
    return (dispatch, getState) => {
        const { wishlistId } = getState().returnsReducer;
        return sendUpdateItemForReturnsWizard(data, wishlistId)
            .then(() => {
                dispatch(getReturnsCart());
                return true;
            })
            .catch(handleError.bind(dispatch));
    };
};

export const toggleSelectedReturnItem = (returnItem) => {
    const eventParamObj = {
        eventCategory: 'toggleReturnItems',
        eventAction: 'return Cart',
        processLabels: false,
        customInfo: {
            returnItem: returnItem,
        },
    };
    sendEventTrackingData(eventParamObj);
    return (dispatch, getState) => {
        const { returnItems } = getState().returnsReducer;
        const aux = returnItems.map((item) => {
            if (item.entryId === returnItem.entryId) {
                item.selected = !item.selected;
            }
            return item;
        });

        dispatch(setReturnItems(aux));
    };
};

export const toggleAllSelectedReturnItems = (newVal) => {
    const eventParamObj = {
        eventCategory: 'toggleAllReturnItems',
        eventAction: 'Return Cart',
        processLabels: false,
    };
    sendEventTrackingData(eventParamObj);
    return (dispatch, getState) => {
        const { returnItems } = getState().returnsReducer;
        const aux = returnItems.map((item) => {
            item.selected = newVal;
            return item;
        });

        dispatch(setReturnItems(aux));
    };
};

// region NOTE: Actions for the return functionality on the OrderDetail page 20191127:Alevale
export const changeAmountODReturnItem = (ODReturnItem, value) => {
    return (dispatch, getState) => {
        const { ODReturnItems } = getState().returnsReducer;
        const aux = ODReturnItems.map((item) => {
            if (item.selectableId === ODReturnItem.selectableId) {
                if (item.disabled) {
                    return item;
                }
                if (item.amount === 0 && value === 1) {
                    item.selected = true;
                }
                if (value === 0) {
                    item.selected = false;
                }
                item.amount = value;
            }
            return item;
        });

        dispatch(setODReturnItems(aux));
    };
};

export const toggleSelectedODReturnItem = (ODReturnItem, value) => {
    const eventParamObj = {
        eventCategory: 'toggleSelectedODReturnItem',
        eventAction: 'Return OrderDetail',
        processLabels: false,
    };
    sendEventTrackingData(eventParamObj);
    return (dispatch, getState) => {
        const { ODReturnItems, returnItems } = getState().returnsReducer;
        const aux = ODReturnItems.map((item) => {
            if (!item.amount || item.disabled) {
                return item;
            }
            if (item.selectableId === ODReturnItem.selectableId) {
                item.selected = value;
                const batchId = returnItems.some((entry) => entry.batchNo === ODReturnItem.batchId);
                item.duplicateItem = batchId && value ? value : false;
            }
            return item;
        });

        dispatch(setODReturnItems(aux));
    };
};

export const toggleAllSelectedODReturnItems = (newVal) => {
    const eventParamObj = {
        eventCategory: 'toggleAllSelectedODReturnItems',
        eventAction: 'Return OrderDetail',
        processLabels: false,
    };
    sendEventTrackingData(eventParamObj);
    return (dispatch, getState) => {
        const { ODReturnItems, returnItems } = getState().returnsReducer;
        const aux = ODReturnItems.map((item) => {
            if (!item.amount || item.disabled) {
                return item;
            }
            item.selected = newVal;
            const batchId = returnItems.some((entry) => entry.batchNo === item.batchId);
            item.duplicateItem = batchId && newVal ? newVal : false;
            return item;
        });

        dispatch(setODReturnItems(aux));
    };
};

export const getReturnEligible = (order) => {
    return (dispatch, getState) => {
        const { wishlistId } = getState().returnsReducer;

        return fetchReturnEligible(
            {
                soldtoId: get(order, 'soldTo.id', ''),
                payerId: get(order, 'payerTo.id', ''),
                billToId: get(order, 'billTo.id', ''),
                supplierId: get(order, 'supplier.id', ''),
                purchaseOrderNumber: get(order, 'orderPO', ''),
            },
            {
                wishlistId: wishlistId,
            }
        );
    };
};
// endregion
